import React, { useEffect, useMemo, useState } from 'react';
import {
  Users, TrendingUp, Star, Linkedin, Copy, ChevronDown, ChevronUp,
  RefreshCw, ExternalLink, MessageSquare, Filter, Plus, X,
} from 'lucide-react';
import { supabase } from '../../../services/supabase/supabaseClient.js';

const SEGMENTOS = [
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'gig',        label: 'Gig' },
  { value: 'transporte', label: 'Transporte' },
  { value: 'logistica',  label: 'Logística' },
];

const PRODUCTOS = [
  'Seguro Complementario',
  'Seguro de Vida',
  'SOAP',
  'Accidentes Personales',
  'Seguro Colectivo',
];

const FORM_INIT = {
  nombre_completo: '',
  titulo_actual: '',
  ubicacion: '',
  linkedin_url: '',
  segmento: 'freelancer',
  sector: '',
  seniority: '',
  ingresos_estimados: '',
  empleados_estimados: '',
  primas_estimadas: '',
  productos_interes: [],
  fit_seguro: 'medio',
  score_prioridad: '',
  senales_freelancer: '',
  razones_fit: '',
  flags_negativos: '',
  notas_agente: '',
  estado_prospecto: 'nuevo',
};

// ── Utilidades ──────────────────────────────────────────────────────────────

const fitConfig = {
  alto:  { label: 'Alto',  classes: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  medio: { label: 'Medio', classes: 'bg-amber-100  text-amber-800  border border-amber-200' },
  bajo:  { label: 'Bajo',  classes: 'bg-red-100    text-red-800    border border-red-200' },
};

const segmentoConfig = {
  freelancer: { label: 'Freelancer', classes: 'bg-[#E6F3F1] text-[#007D6E] border border-[#007D6E]/20' },
  gig:        { label: 'Gig',        classes: 'bg-purple-100 text-purple-800 border border-purple-200' },
  transporte: { label: 'Transporte', classes: 'bg-sky-100 text-sky-800 border border-sky-200' },
  logistica:  { label: 'Logística',  classes: 'bg-orange-100 text-orange-800 border border-orange-200' },
};

const estadoConfig = {
  nuevo:      { label: 'Nuevo',      classes: 'bg-blue-100 text-blue-800 border border-blue-200' },
  contactado: { label: 'Contactado', classes: 'bg-teal-100 text-teal-800 border border-teal-200' },
  descartado: { label: 'Descartado', classes: 'bg-gray-100 text-gray-600 border border-gray-200' },
};

const FitBadge = ({ value }) => {
  const cfg = fitConfig[value?.toLowerCase()] ?? fitConfig.bajo;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

const SegmentoBadge = ({ value }) => {
  if (!value) return <span className="text-xs text-slate-400">—</span>;
  const cfg = segmentoConfig[value.toLowerCase()] ?? { label: value, classes: 'bg-slate-100 text-slate-600 border border-slate-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

const EstadoBadge = ({ value }) => {
  const cfg = estadoConfig[value?.toLowerCase()] ?? estadoConfig.nuevo;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

const ScoreBar = ({ value }) => {
  const score = Number(value) || 0;
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-200">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 w-6 text-right">{score}</span>
    </div>
  );
};

// ── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4 shadow-sm">
    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ── Modal crear prospecto ─────────────────────────────────────────────────────

const CreateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleProducto = (p) => {
    setForm((prev) => ({
      ...prev,
      productos_interes: prev.productos_interes.includes(p)
        ? prev.productos_interes.filter((x) => x !== p)
        : [...prev.productos_interes, p],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre_completo.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true);
    setErr(null);
    try {
      const { error: err } = await supabase.from('prospectos').insert({
        ...form,
        productos_interes: form.productos_interes.join(', '),
      });
      if (err) throw err;
      onCreated();
      onClose();
    } catch (ex) {
      setErr(ex?.message || 'Error al crear el prospecto');
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30 focus:border-[#007D6E] bg-white';
  const lbl = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1';
  const sec = 'border-t border-slate-100 pt-5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Nuevo prospecto</h2>
            <p className="text-sm text-slate-500 mt-0.5">Completa los datos del prospecto</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Datos básicos */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Datos básicos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={lbl}>Nombre completo *</label>
                <input className={inp} value={form.nombre_completo} onChange={(e) => set('nombre_completo', e.target.value)} placeholder="Ej. Juan Pérez" />
              </div>
              <div>
                <label className={lbl}>Título / cargo</label>
                <input className={inp} value={form.titulo_actual} onChange={(e) => set('titulo_actual', e.target.value)} placeholder="Ej. Conductor independiente" />
              </div>
              <div>
                <label className={lbl}>Ubicación</label>
                <input className={inp} value={form.ubicacion} onChange={(e) => set('ubicacion', e.target.value)} placeholder="Ej. Santiago, Chile" />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>LinkedIn URL</label>
                <input className={inp} value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." />
              </div>
            </div>
          </div>

          {/* Segmento y perfil */}
          <div className={sec}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Segmento y perfil</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Segmento</label>
                <select className={inp} value={form.segmento} onChange={(e) => set('segmento', e.target.value)}>
                  {SEGMENTOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Sector / industria</label>
                <input className={inp} value={form.sector} onChange={(e) => set('sector', e.target.value)} placeholder="Ej. Delivery, Construcción" />
              </div>
              <div>
                <label className={lbl}>Seniority</label>
                <input className={inp} value={form.seniority} onChange={(e) => set('seniority', e.target.value)} placeholder="Junior / Senior / Independiente" />
              </div>
              <div>
                <label className={lbl}>Ingresos estimados</label>
                <input className={inp} value={form.ingresos_estimados} onChange={(e) => set('ingresos_estimados', e.target.value)} placeholder="Ej. $800.000/mes" />
              </div>
            </div>
          </div>

          {/* Potencial de negocio */}
          <div className={sec}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Potencial de negocio</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Empleados estimados</label>
                <input type="number" min="0" className={inp} value={form.empleados_estimados} onChange={(e) => set('empleados_estimados', e.target.value)} placeholder="Nº de empleados o trabajadores" />
              </div>
              <div>
                <label className={lbl}>Prima estimada mensual</label>
                <input className={inp} value={form.primas_estimadas} onChange={(e) => set('primas_estimadas', e.target.value)} placeholder="Ej. $50.000/mes" />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Productos de interés</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PRODUCTOS.map((p) => (
                    <button key={p} type="button" onClick={() => toggleProducto(p)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        form.productos_interes.includes(p)
                          ? 'bg-[#007D6E] text-white border-[#007D6E]'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-[#007D6E] hover:text-[#007D6E]'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Evaluación */}
          <div className={sec}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Evaluación</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Fit seguro</label>
                <select className={inp} value={form.fit_seguro} onChange={(e) => set('fit_seguro', e.target.value)}>
                  <option value="alto">Alto</option>
                  <option value="medio">Medio</option>
                  <option value="bajo">Bajo</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Score de prioridad (0–100)</label>
                <input type="number" min="0" max="100" className={inp} value={form.score_prioridad} onChange={(e) => set('score_prioridad', e.target.value)} placeholder="Ej. 75" />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Señales del segmento</label>
                <textarea className={`${inp} resize-none`} rows={2} value={form.senales_freelancer} onChange={(e) => set('senales_freelancer', e.target.value)} placeholder="Señales que indican interés o fit con el producto..." />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Razones de fit</label>
                <textarea className={`${inp} resize-none`} rows={2} value={form.razones_fit} onChange={(e) => set('razones_fit', e.target.value)} placeholder="¿Por qué encaja con nuestros productos?" />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Flags negativos</label>
                <textarea className={`${inp} resize-none`} rows={2} value={form.flags_negativos} onChange={(e) => set('flags_negativos', e.target.value)} placeholder="Dudas o razones de exclusión potencial..." />
              </div>
            </div>
          </div>

          {/* Estado */}
          <div className={sec}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Estado</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Estado del prospecto</label>
                <select className={inp} value={form.estado_prospecto} onChange={(e) => set('estado_prospecto', e.target.value)}>
                  <option value="nuevo">Nuevo</option>
                  <option value="contactado">Contactado</option>
                  <option value="descartado">Descartado</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Notas del agente</label>
                <textarea className={`${inp} resize-none`} rows={3} value={form.notas_agente} onChange={(e) => set('notas_agente', e.target.value)} placeholder="Fuente del prospecto, contexto, observaciones..." />
              </div>
            </div>
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{err}</div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-lg bg-[#007D6E] text-white text-sm font-semibold hover:bg-[#006358] transition-colors disabled:opacity-50">
              {saving ? 'Guardando...' : 'Crear prospecto'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal detalle / acción ───────────────────────────────────────────────────

const DetailModal = ({ prospect, onClose, onCopy }) => {
  if (!prospect) return null;
  const f = prospect;
  const productosArr = f.productos_interes
    ? f.productos_interes.split(',').map((p) => p.trim()).filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{f.nombre_completo}</h2>
              <p className="text-sm text-slate-500">{f.titulo_actual}</p>
              <p className="text-xs text-slate-400 mt-0.5">{f.ubicacion}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {f.segmento && <SegmentoBadge value={f.segmento} />}
              <FitBadge value={f.fit_seguro} />
              <EstadoBadge value={f.estado_prospecto} />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Score + datos clave */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Score</span>
              <div className="mt-1"><ScoreBar value={f.score_prioridad} /></div>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Sector</span>
              <p className="mt-1 text-slate-800">{f.sector || '—'}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Seniority</span>
              <p className="mt-1 text-slate-800">{f.seniority || '—'}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ingresos</span>
              <p className="mt-1 text-slate-800">{f.ingresos_estimados || '—'}</p>
            </div>
          </div>

          {/* Potencial de negocio */}
          {(f.empleados_estimados || f.primas_estimadas || productosArr.length > 0) && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Potencial de negocio</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {f.empleados_estimados && (
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500">Empleados estimados</p>
                    <p className="text-xl font-bold text-slate-800 mt-0.5">{f.empleados_estimados}</p>
                  </div>
                )}
                {f.primas_estimadas && (
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500">Prima estimada / mes</p>
                    <p className="text-xl font-bold text-[#007D6E] mt-0.5">{f.primas_estimadas}</p>
                  </div>
                )}
              </div>
              {productosArr.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-1.5">Productos de interés</p>
                  <div className="flex flex-wrap gap-1.5">
                    {productosArr.map((p, i) => (
                      <span key={i} className="px-2.5 py-1 bg-[#E6F3F1] text-[#007D6E] text-xs rounded-full font-medium">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Señales */}
          {f.senales_freelancer && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Señales</p>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{f.senales_freelancer}</p>
            </div>
          )}

          {/* Razones fit */}
          {f.razones_fit && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Razones de fit</p>
              <p className="text-sm text-slate-700 bg-emerald-50 rounded-lg p-3 whitespace-pre-line">{f.razones_fit}</p>
            </div>
          )}

          {/* Flags negativos */}
          {f.flags_negativos && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Flags negativos</p>
              <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">{f.flags_negativos}</p>
            </div>
          )}

          {/* Mensaje sugerido */}
          {f.mensaje_sugerido && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Mensaje LinkedIn</p>
                <button
                  onClick={() => onCopy(f.mensaje_sugerido)}
                  className="flex items-center gap-1 text-xs text-[#007D6E] hover:text-[#006358] font-medium"
                >
                  <Copy className="w-3 h-3" />
                  Copiar
                </button>
              </div>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-line leading-relaxed">
                {f.asunto_mensaje && (
                  <span className="block font-semibold text-slate-900 mb-2">Asunto: {f.asunto_mensaje}</span>
                )}
                {f.mensaje_sugerido}
              </p>
            </div>
          )}

          {/* Notas agente */}
          {f.notas_agente && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Notas del agente</p>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{f.notas_agente}</p>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="p-6 border-t border-slate-100 flex items-center gap-3">
          {f.linkedin_url && (
            <a
              href={f.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] text-white text-sm font-medium hover:bg-[#004182] transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              Abrir LinkedIn
            </a>
          )}
          {f.mensaje_sugerido && (
            <button
              onClick={() => onCopy(f.mensaje_sugerido)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#007D6E] text-white text-sm font-medium hover:bg-[#006358] transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copiar mensaje
            </button>
          )}
          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────

const ProspectosFreelancerPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filterFit, setFilterFit] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterSegmento, setFilterSegmento] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('score_prioridad');
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('prospectos')
        .select('*')
        .order('score_prioridad', { ascending: false });
      if (err) throw err;
      setRecords(data || []);
    } catch (e) {
      setError(e?.message || 'Error al cargar prospectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (filterFit && r.fit_seguro?.toLowerCase() !== filterFit) return false;
      if (filterEstado && r.estado_prospecto?.toLowerCase() !== filterEstado) return false;
      if (filterSegmento && r.segmento?.toLowerCase() !== filterSegmento) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const haystack = [r.nombre_completo, r.titulo_actual, r.sector, r.ubicacion, r.segmento]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [records, filterFit, filterEstado, filterSegmento, searchText]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? '';
      const vb = b[sortField] ?? '';
      const na = Number(va);
      const nb = Number(vb);
      const isNum = !isNaN(na) && !isNaN(nb);
      const cmp = isNum ? na - nb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir]);

  // KPIs
  const kpis = useMemo(() => {
    const all = records;
    const total = all.length;
    const altoFit = all.filter((f) => f.fit_seguro?.toLowerCase() === 'alto').length;
    const scores = all.map((f) => Number(f.score_prioridad) || 0).filter((n) => n > 0);
    const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
    const contactados = all.filter((f) => f.estado_prospecto?.toLowerCase() === 'contactado').length;
    return { total, altoFit, avgScore, contactados };
  }, [records]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const hasFilters = filterFit || filterEstado || filterSegmento || searchText;
  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap select-none';
  const thBtn = 'flex items-center gap-1 hover:text-slate-900 transition-colors cursor-pointer';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prospectos</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Freelancers, gig, transporte y logística con fit para seguros
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#007D6E] text-white text-sm font-semibold hover:bg-[#006358] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo prospecto
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Total prospectos" value={kpis.total} sub="todos los segmentos" accent="bg-[#E6F3F1] text-[#007D6E]" />
        <KpiCard icon={Star} label="Fit alto" value={kpis.altoFit} sub={`${kpis.total ? Math.round((kpis.altoFit / kpis.total) * 100) : 0}% del total`} accent="bg-emerald-100 text-emerald-700" />
        <KpiCard icon={TrendingUp} label="Score promedio" value={kpis.avgScore} sub="/ 100 puntos" accent="bg-indigo-100 text-indigo-700" />
        <KpiCard icon={MessageSquare} label="Contactados" value={kpis.contactados} sub={`${kpis.total - kpis.contactados} pendientes`} accent="bg-amber-100 text-amber-700" />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Buscar por nombre, sector, cargo..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30 focus:border-[#007D6E]"
        />
        <select value={filterSegmento} onChange={(e) => setFilterSegmento(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30 bg-white">
          <option value="">Todos los segmentos</option>
          {SEGMENTOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterFit} onChange={(e) => setFilterFit(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30 bg-white">
          <option value="">Todos los fits</option>
          <option value="alto">Fit alto</option>
          <option value="medio">Fit medio</option>
          <option value="bajo">Fit bajo</option>
        </select>
        <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007D6E]/30 bg-white">
          <option value="">Todos los estados</option>
          <option value="nuevo">Nuevo</option>
          <option value="contactado">Contactado</option>
          <option value="descartado">Descartado</option>
        </select>
        {hasFilters && (
          <button
            onClick={() => { setFilterFit(''); setFilterEstado(''); setFilterSegmento(''); setSearchText(''); }}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Limpiar
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">{sorted.length} resultados</span>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-sm text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Cargando prospectos...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-48 text-sm text-red-500">{error}</div>
        ) : sorted.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-sm text-slate-400">
            No hay prospectos que coincidan con los filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className={thClass}>
                    <button className={thBtn} onClick={() => handleSort('nombre_completo')}>
                      Prospecto <SortIcon field="nombre_completo" />
                    </button>
                  </th>
                  <th className={thClass}>Segmento</th>
                  <th className={thClass}>Sector</th>
                  <th className={thClass}>
                    <button className={thBtn} onClick={() => handleSort('fit_seguro')}>
                      Fit <SortIcon field="fit_seguro" />
                    </button>
                  </th>
                  <th className={thClass}>
                    <button className={thBtn} onClick={() => handleSort('score_prioridad')}>
                      Score <SortIcon field="score_prioridad" />
                    </button>
                  </th>
                  <th className={thClass}>Estado</th>
                  <th className={thClass}>Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((record) => {
                  const f = record;
                  return (
                    <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="max-w-[220px]">
                          <p className="text-sm font-semibold text-slate-900 truncate">{f.nombre_completo}</p>
                          <p className="text-xs text-slate-500 truncate">{f.titulo_actual}</p>
                          <p className="text-xs text-slate-400 truncate">{f.ubicacion}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <SegmentoBadge value={f.segmento} />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-slate-600 max-w-[140px] line-clamp-2">{f.sector || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <FitBadge value={f.fit_seguro} />
                      </td>
                      <td className="px-4 py-3">
                        <ScoreBar value={f.score_prioridad} />
                      </td>
                      <td className="px-4 py-3">
                        <EstadoBadge value={f.estado_prospecto} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {f.linkedin_url && (
                            <a href={f.linkedin_url} target="_blank" rel="noopener noreferrer" title="Abrir LinkedIn" className="p-1.5 rounded-md text-[#0A66C2] hover:bg-blue-50 transition-colors">
                              <Linkedin className="w-4 h-4" />
                            </a>
                          )}
                          {f.mensaje_sugerido && (
                            <button onClick={() => handleCopy(f.mensaje_sugerido)} title="Copiar mensaje DM" className="p-1.5 rounded-md text-[#007D6E] hover:bg-[#E6F3F1] transition-colors">
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => setSelected(record)} title="Ver detalle" className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 transition-colors">
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {copied && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg">
          <Copy className="w-3.5 h-3.5" />
          Mensaje copiado al portapapeles
        </div>
      )}

      <DetailModal prospect={selected} onClose={() => setSelected(null)} onCopy={handleCopy} />

      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />
      )}
    </div>
  );
};

export default ProspectosFreelancerPage;
